import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight,
  Clock,
  CheckCircle,
  XCircle,
  Hourglass,
  Prohibit,
  DownloadSimple,
} from "@phosphor-icons/react";
import Drawer from "@/components/Drawer";
import DeliveryChallanButton from "@/components/DeliveryChallanButton";
import { fmtDate } from "@/lib/format";
import useBranches from "@/hooks/useBranches";
import { useAuth } from "@/context/AuthContext";

const STATUS_META = {
  pending:   { label: "Pending",   icon: Hourglass,    color: "text-[#B58900]", bg: "bg-[#B58900]/10" },
  approved:  { label: "Approved",  icon: CheckCircle,  color: "text-[#00B341]", bg: "bg-[#00B341]/10" },
  rejected:  { label: "Rejected",  icon: XCircle,      color: "text-destructive", bg: "bg-destructive/10" },
  cancelled: { label: "Cancelled", icon: Prohibit,     color: "text-ink-muted",   bg: "bg-canvas-panel" },
  reversed:  { label: "Reversed",  icon: Prohibit,     color: "text-[#a11]",       bg: "bg-[#a11]/10" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.approved;
  const Icon = m.icon;
  return (
    <span
      data-testid={`transfer-status-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-1 ${m.bg} ${m.color} font-mono text-[10px] uppercase tracking-wider`}
    >
      <Icon size={11} weight="bold" />
      {m.label}
    </span>
  );
}

function TransferCard({ t, byId, currentUser, onAction }) {
  const isPending = t.status === "pending";
  const isApproved = t.status === "approved";
  const canApprove =
    isPending &&
    (currentUser.role === "admin" ||
      byId[t.to_branch_id]?.manager_user_id === currentUser.id);
  const canCancel =
    isPending && (currentUser.role === "admin" || t.by_user_id === currentUser.id);
  const canReverse = isApproved && currentUser.role === "admin";

  const handleReject = async () => {
    const reason = window.prompt("Optional rejection reason:") || "";
    await onAction(t.id, "reject", { reason });
  };

  const handleReverse = async () => {
    const reason = window.prompt(
      "Why are you reversing this approved transfer? (e.g. \"Should have been a sub-dealer invoice\")"
    );
    if (reason === null) return;
    if (
      !window.confirm(
        `Reverse ${t.count} vehicle(s) back to the source branch? The transfer will be marked "Reversed". Only works if none of the vehicles have been sold onward.`
      )
    ) return;
    await onAction(t.id, "reverse", { reason });
  };

  return (
    <article
      data-testid={`transfer-row-${t.id}`}
      className="border border-canvas-line p-4 bg-canvas-card"
    >
      <header className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-ink">
          <span className="font-semibold">
            {byId[t.from_branch_id]?.name || "Unassigned"}
          </span>
          <ArrowRight size={12} weight="bold" className="text-ink-muted" />
          <span className="font-semibold">{byId[t.to_branch_id]?.name || "—"}</span>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={t.status} />
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
            {t.count} vehicle{t.count === 1 ? "" : "s"}
          </span>
        </div>
      </header>

      <ul className="space-y-1 mb-3">
        {(t.vehicle_summaries || []).slice(0, 6).map((v) => {
          // Prefer the live package name (the real variant e.g. "JUPITER 125
          // BSVI-DISC") over the raw `model` field which is sometimes a
          // generic category like "SCOOTERS" / "MOPED".
          const label =
            (v.package_names || []).join(" · ") ||
            v.color || v.year ||
            "Vehicle";
          return (
            <li key={v.id} className="font-mono text-[11px] text-ink-muted">
              <span className="text-ink">{label}</span> — {v.vin}
            </li>
          );
        })}
        {(t.vehicle_summaries || []).length > 6 && (
          <li className="font-mono text-[11px] text-ink-subtle">
            + {t.vehicle_summaries.length - 6} more…
          </li>
        )}
      </ul>

      {t.note && (
        <div className="text-xs text-ink mb-2 italic border-l-2 border-canvas-line pl-2">
          “{t.note}”
        </div>
      )}

      {t.decision_reason && (
        <div className="text-xs text-ink-muted mb-2 italic border-l-2 border-canvas-line pl-2">
          Decision reason: {t.decision_reason}
        </div>
      )}

      <footer className="flex items-center justify-between text-[11px] font-mono text-ink-muted pt-2 border-t border-canvas-line">
        <div className="flex flex-col gap-0.5">
          <span>by {t.by_user_name || t.by_user_email}</span>
          {t.decided_by_user_name && t.status !== "pending" && (
            <span>
              {t.status} by {t.decided_by_user_name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <DeliveryChallanButton transferId={t.id} />
          <span className="inline-flex items-center gap-1">
            <Clock size={11} weight="bold" /> {fmtDate(t.decided_at || t.created_at)}
          </span>
        </div>
      </footer>

      {(isPending || canReverse) && (canApprove || canCancel || canReverse) && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-canvas-line">
          {canApprove && (
            <>
              <button
                data-testid={`approve-transfer-${t.id}`}
                onClick={() => onAction(t.id, "approve", {})}
                className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand"
              >
                Approve
              </button>
              <button
                data-testid={`reject-transfer-${t.id}`}
                onClick={handleReject}
                className="px-3 py-1.5 border border-canvas-line text-xs font-semibold hover:border-destructive hover:text-destructive"
              >
                Reject
              </button>
            </>
          )}
          {canCancel && (
            <button
              data-testid={`cancel-transfer-${t.id}`}
              onClick={() => onAction(t.id, "cancel", {})}
              className="px-3 py-1.5 border border-canvas-line text-xs hover:border-ink ml-auto"
            >
              Cancel transfer
            </button>
          )}
          {canReverse && (
            <button
              data-testid={`reverse-transfer-${t.id}`}
              onClick={handleReverse}
              title="Undo — sends vehicles back to source branch. Use only when the move should have been a sub-dealer invoice instead."
              className="px-3 py-1.5 border border-[#a11] text-[#a11] text-xs font-semibold hover:bg-[#a11] hover:text-white ml-auto"
            >
              Reverse transfer
            </button>
          )}
        </div>
      )}
    </article>
  );
}

export default function TransferHistoryDrawer({ open, onClose, refreshKey }) {
  const { user } = useAuth();
  const { byId } = useBranches();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/inventory/transfers", { params });
      setRows(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (open) load();
  }, [open, refreshKey, load]);

  const handleAction = async (id, action, body) => {
    try {
      const { data } = await api.post(`/inventory/transfers/${id}/${action}`, body);
      toast.success(
        action === "approve" ? "Transfer approved" :
        action === "reject"  ? "Transfer rejected" :
        "Transfer cancelled"
      );
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleExportCsv = () => {
    const params = new URLSearchParams();
    if (statusFilter) params.append("status", statusFilter);
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    api
      .get(`/inventory/transfers.csv?${params.toString()}`, { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "vehicle_transfers.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("CSV downloaded");
      })
      .catch((err) => toast.error(formatApiError(err)));
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="Audit log"
      title="Vehicle Transfer History"
      testIdClose="close-transfer-history"
    >
      <div className="mb-4 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <select
            data-testid="filter-transfer-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-2 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
          <input
            type="date"
            data-testid="filter-transfer-from"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-2 py-2 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
            title="From date"
          />
          <input
            type="date"
            data-testid="filter-transfer-to"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-2 py-2 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
            title="To date"
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
            {loading ? "Loading…" : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
          </span>
          <button
            data-testid="export-transfers-csv"
            onClick={handleExportCsv}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-canvas-line text-[11px] font-mono uppercase tracking-wider hover:border-ink"
          >
            <DownloadSimple size={12} weight="bold" />
            Export CSV
          </button>
        </div>
      </div>

      <div data-testid="transfer-history-list" className="space-y-3">
        {!loading && rows.length === 0 && (
          <div className="border border-dashed border-canvas-line p-8 text-center">
            <div className="font-mono text-xs text-ink-subtle">
              No transfers match your filters.
            </div>
          </div>
        )}
        {rows.map((t) => (
          <TransferCard
            key={t.id}
            t={t}
            byId={byId}
            currentUser={user}
            onAction={handleAction}
          />
        ))}
      </div>
    </Drawer>
  );
}
